/**
 * View toolbar — tab triggers + live preview pin controls. Which tabs exist
 * is the tracker's drawer profile (no Queue / Tags for Tres Commas).
 */

import { FC, ReactNode } from 'react';
import {
  Activity,
  Eye,
  LayoutGrid,
  ListChecks,
  ListOrdered,
  Package as PackageIcon,
  Settings,
  Settings2,
  Sigma,
  Sliders,
  SlidersHorizontal,
  Tags,
} from 'lucide-react';

import { TabsList, TabsTrigger } from '@/components/ui/layout/Tabs';

import { ToolbarRowLabel } from './components';
import { PreviewToolbarControls } from './components/preview/panel';
import type { DrawerProfile } from '@/features/flow-optimizer/tracker';

const triggerClass =
  'h-7 gap-1 px-2.5 py-0 text-[11px] data-[state=active]:bg-background data-[state=active]:shadow-sm';

export interface FlowOptimizerViewToolbarProps {
  isCustom: boolean;
  profile: DrawerProfile;
  showPreviewControls: boolean;
  previewPinned: boolean;
  onTogglePreviewPin: () => void;
  statusSlot: ReactNode;
  modeSlot: ReactNode;
  /** Library package picker (CUSTOM only) — composed by the drawer. */
  librarySlot?: ReactNode;
}

const FlowOptimizerViewToolbar: FC<FlowOptimizerViewToolbarProps> = ({
  isCustom,
  profile,
  showPreviewControls,
  previewPinned,
  onTogglePreviewPin,
  statusSlot,
  modeSlot,
  librarySlot,
}) => (
  <div className="flex shrink-0 flex-col gap-1">
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-border/70 bg-muted/20 px-2 py-1.5">
      <div className="flex min-w-0 items-center gap-2">
        <ToolbarRowLabel icon={Activity}>Status</ToolbarRowLabel>
        {statusSlot}
      </div>
      <span aria-hidden className="hidden h-4 w-px shrink-0 bg-border/60 sm:block" />
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:flex-initial">
        <ToolbarRowLabel icon={Settings2}>Mode</ToolbarRowLabel>
        {modeSlot}
      </div>
      {isCustom && librarySlot ? (
        <>
          <span aria-hidden className="hidden h-4 w-px shrink-0 bg-border/60 sm:block" />
          <div className="flex min-w-0 items-center gap-2">
            <ToolbarRowLabel icon={PackageIcon}>Package</ToolbarRowLabel>
            {librarySlot}
          </div>
        </>
      ) : null}
    </div>
    <div className="flex h-9 shrink-0 items-center gap-2 rounded-md border border-border/60 bg-muted/15 px-2">
      <ToolbarRowLabel icon={LayoutGrid}>View</ToolbarRowLabel>
      <TabsList
        aria-label="Flow Optimizer views"
        className="h-7 min-w-0 flex-1 justify-start gap-0.5 bg-transparent p-0"
      >
        {isCustom ? (
          <TabsTrigger value="calculation" className={triggerClass}>
            <Sigma className="h-3 w-3 shrink-0" aria-hidden />
            Formula
          </TabsTrigger>
        ) : null}
        {isCustom ? (
          <TabsTrigger value="rules" className={triggerClass}>
            <ListChecks className="h-3 w-3 shrink-0" aria-hidden />
            Rules
          </TabsTrigger>
        ) : null}
        {isCustom && profile.queue ? (
          <TabsTrigger value="queue" className={triggerClass}>
            <ListOrdered className="h-3 w-3 shrink-0" aria-hidden />
            Queue
          </TabsTrigger>
        ) : null}
        {isCustom && profile.tagsTab ? (
          <TabsTrigger value="tags" className={triggerClass}>
            <Tags className="h-3 w-3 shrink-0" aria-hidden />
            Tags
          </TabsTrigger>
        ) : null}
        {isCustom ? (
          <TabsTrigger value="preview" className={triggerClass}>
            <Eye className="h-3 w-3 shrink-0" aria-hidden />
            Preview
          </TabsTrigger>
        ) : (
          <TabsTrigger value="standard" className={triggerClass}>
            <SlidersHorizontal className="h-3 w-3 shrink-0" aria-hidden />
            Standard
          </TabsTrigger>
        )}
        {profile.campaignTabs ? (
          <TabsTrigger value="campaign" className={triggerClass}>
            <Settings className="h-3 w-3 shrink-0" aria-hidden />
            Campaign
          </TabsTrigger>
        ) : null}
        {profile.campaignTabs ? (
          <TabsTrigger value="advanced" className={triggerClass}>
            <Sliders className="h-3 w-3 shrink-0" aria-hidden />
            Advanced
          </TabsTrigger>
        ) : null}
      </TabsList>
      {showPreviewControls ? (
        <PreviewToolbarControls
          pinned={previewPinned}
          onTogglePin={onTogglePreviewPin}
        />
      ) : null}
    </div>
  </div>
);

export default FlowOptimizerViewToolbar;
